import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import React from "react";
import PropTypes from "prop-types";
import useAppContextProvider from "../../AppContext/useAppContextProvider.js";
import { Box } from "@mui/material";
import {
  BodySmall,
  H8TitleMedium,
} from "../../components/StyledComponents/Typography/Typography.tsx";
import { IconButton } from "@mui/material";
import OpenInFullOutlinedIcon from "@mui/icons-material/OpenInFullOutlined";
import CloseFullscreenOutlinedIcon from "@mui/icons-material/CloseFullscreenOutlined";
import { useTheme } from "../../ThemeContext/ThemeContext.js";

const LineChart = (props) => {
  const {
    heading,
    subHeading,
    lineChartOptions,
    hasIssuesData,
    isExpanded,
    isExpansionRequired,
    chartIndex,
    setExpandedChartIndex,
    expandedChartIndex,
    isExpandedGraphsModeActivated,
  } = props;
  const { selectedValue } = useAppContextProvider();
  const { colors } = useTheme();
  if (!lineChartOptions) return <> </>;

  const expansionHandler = (index) => {
    setExpandedChartIndex(index);
  };

  const minimizerHandler = () => {
    setExpandedChartIndex(-1);
  };

  // Apply theme-aware background colors
  const themedChartOptions = {
    ...lineChartOptions,
    chart: {
      ...lineChartOptions.chart,
      backgroundColor: colors.background.card,
      plotBackgroundColor: colors.background.card,
    },
    // Apply theme colors to text elements
    title: {
      ...lineChartOptions.title,
      style: {
        ...lineChartOptions.title?.style,
        color: colors.text.primary,
      },
    },
    subtitle: {
      ...lineChartOptions.subtitle,
      style: {
        ...lineChartOptions.subtitle?.style,
        color: colors.text.secondary,
      },
    },
    // Apply theme colors to axes
    xAxis: {
      ...lineChartOptions.xAxis,
      labels: {
        ...lineChartOptions.xAxis?.labels,
        style: {
          ...lineChartOptions.xAxis?.labels?.style,
          color: colors.text.muted,
        },
      },
      title: {
        ...lineChartOptions.xAxis?.title,
        style: {
          ...lineChartOptions.xAxis?.title?.style,
          color: colors.text.secondary,
        },
      },
      lineColor: colors.border.light,
      tickColor: colors.border.light,
      gridLineColor: colors.border.muted,
    },
    yAxis: {
      ...lineChartOptions.yAxis,
      labels: {
        ...lineChartOptions.yAxis?.labels,
        style: {
          ...lineChartOptions.yAxis?.labels?.style,
          color: colors.text.muted,
        },
      },
      title: {
        ...lineChartOptions.yAxis?.title,
        style: {
          ...lineChartOptions.yAxis?.title?.style,
          color: colors.text.secondary,
        },
      },
      gridLineColor: colors.border.muted,
    },
    // Apply theme colors to legend
    legend: {
      ...lineChartOptions.legend,
      itemStyle: {
        ...lineChartOptions.legend?.itemStyle,
        color: colors.text.primary,
      },
      itemHoverStyle: {
        ...lineChartOptions.legend?.itemHoverStyle,
        color: colors.text.secondary,
      },
    },
  };

  if (isExpanded) {
    themedChartOptions.chart.height = 260;
  }
  if (
    expandedChartIndex !== -1 &&
    isExpandedGraphsModeActivated &&
    !isExpanded
  ) {
    themedChartOptions.chart.height = 97;
    themedChartOptions.legend.enabled = false;
    themedChartOptions.xAxis.labels.enabled = false;
    themedChartOptions.xAxis.tickWidth = 0;
  }

  return (
    <>
      <Box
        display={"flex"}
        flexDirection={"row"}
        justifyContent={"space-between"}
        padding={
          expandedChartIndex !== -1 &&
          isExpandedGraphsModeActivated &&
          !isExpanded
            ? "0.313rem 0.75rem"
            : "0.675rem 1.5rem"
        }
      >
        <Box
          display="flex"
          flexDirection="column"
          alignItems="start"
          gap={1}
        >
          <H8TitleMedium>{heading}</H8TitleMedium>
          <BodySmall>{subHeading}</BodySmall>
        </Box>
        {isExpansionRequired && (
          <Box
            display={"flex"}
            flexDirection={"column"}
            justifyContent={"center"}
            alignItems={"center"}
            sx={{ maxHeight: "1.5rem", maxWidth: "1.5rem" }}
          >
            {!isExpanded ? (
              <IconButton
                onClick={() => expansionHandler(chartIndex)}
              >
                <OpenInFullOutlinedIcon sx={{ color: colors.text.muted }}/>
              </IconButton>
            ) : (
              <IconButton onClick={minimizerHandler}>
                <CloseFullscreenOutlinedIcon sx={{ color: colors.text.muted }}/>
              </IconButton>
            )}
          </Box>
        )}
      </Box>
      {hasIssuesData === false && heading === "Issues Over Time" ? (
        // Show "No issues" message when there's no data for the issues over time graph
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="75px"
          width="100%"
        >
          <H8TitleMedium style={{ color: colors.text.muted }}>
            No issues were detected during the selected period
          </H8TitleMedium>
        </Box>
      ) : (
        <HighchartsReact
          highcharts={Highcharts}
          options={
            isExpanded &&
            ["today", "last 7 days"].includes(
              selectedValue?.toLowerCase()
            )
              ? {
                  ...themedChartOptions,
                  chart: {
                    ...themedChartOptions.chart,
                    zooming: { type: "x" },
                  },
                }
              : themedChartOptions
          }
          containerProps={{
            style: { height: "100%", width: "100%" },
          }}
        />
      )}
    </>
  );
};

LineChart.defaultProps = {
  isExpanded: false,
  isExpansionRequired: false,
};

LineChart.propTypes = {
  heading: PropTypes.string.isRequired,
  subHeading: PropTypes.string.isRequired,
  lineChartOptions: PropTypes.object.isRequired,
  hasIssuesData: PropTypes.bool,
  isExpanded: PropTypes.bool,
  isExpansionRequired: PropTypes.bool,
  chartIndex: PropTypes.number,
  isExpandedGraphsModeActivated: PropTypes.bool,
  expandedChartIndex: PropTypes.number,
  selectedValue: PropTypes.string,
};

export default React.memo(LineChart);
